import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, Package, Home } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { sendOrderNotificationEmail } from '../services/emailService';
import { saveOrder } from '../services/orderService';
import { updateProductQuantity, getProduct } from '../services/api';

const CheckoutSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [orderData, setOrderData] = useState(null);
  const whatsAppOpened = useRef(false);
  const emailSent = useRef(false);
  const orderSaved = useRef(false);
  const stockUpdated = useRef(false);

  const COMPANY_PHONE = '03136261452'; // Company WhatsApp number

  const formatPrice = (price) => {
    if (typeof price === 'string') {
      return price;
    }
    return `Rs ${price.toLocaleString()}`;
  };

  const formatPrescriptionDetails = (prescription, productType) => {
    if (!prescription) return 'None';

    const { rightEye, leftEye } = prescription;
    let hasRightPrescription, hasLeftPrescription;
    
    if (productType === 'lenses') {
      hasRightPrescription = rightEye?.distance?.sph;
      hasLeftPrescription = leftEye?.distance?.sph;
    } else {
      hasRightPrescription = rightEye?.distance?.sph || rightEye?.distance?.cyl || rightEye?.distance?.axis || 
                            rightEye?.near?.sph || rightEye?.near?.cyl || rightEye?.near?.axis;
      hasLeftPrescription = leftEye?.distance?.sph || leftEye?.distance?.cyl || leftEye?.distance?.axis || 
                           leftEye?.near?.sph || leftEye?.near?.cyl || leftEye?.near?.axis;
    }
    
    if (!hasRightPrescription && !hasLeftPrescription) return 'None';

    let prescriptionText = '';
    
    if (hasRightPrescription) {
      prescriptionText += 'Right Eye:\n';
      if (rightEye?.distance?.sph || (productType === 'eyeglasses' && (rightEye?.distance?.cyl || rightEye?.distance?.axis))) {
        if (productType === 'lenses') {
          prescriptionText += `  Distance: SPH: ${rightEye.distance.sph || 'null'}\n`;
        } else {
          prescriptionText += `  Distance: SPH: ${rightEye.distance.sph || 'null'}, CYL: ${rightEye.distance.cyl || 'null'}, AXIS: ${rightEye.distance.axis || 'null'}\n`;
        }
      }
      if (productType === 'eyeglasses' && (rightEye?.near?.sph || rightEye?.near?.cyl || rightEye?.near?.axis)) {
        prescriptionText += `  Near: SPH: ${rightEye.near.sph || 'null'}, CYL: ${rightEye.near.cyl || 'null'}, AXIS: ${rightEye.near.axis || 'null'}\n`;
      }
    }

    if (hasLeftPrescription) {
      prescriptionText += 'Left Eye:\n';
      if (leftEye?.distance?.sph || (productType === 'eyeglasses' && (leftEye?.distance?.cyl || leftEye?.distance?.axis))) {
        if (productType === 'lenses') {
          prescriptionText += `  Distance: SPH: ${leftEye.distance.sph || 'null'}\n`;
        } else {
          prescriptionText += `  Distance: SPH: ${leftEye.distance.sph || 'null'}, CYL: ${leftEye.distance.cyl || 'null'}, AXIS: ${leftEye.distance.axis || 'null'}\n`;
        }
      }
      if (productType === 'eyeglasses' && (leftEye?.near?.sph || leftEye?.near?.cyl || leftEye?.near?.axis)) {
        prescriptionText += `  Near: SPH: ${leftEye.near.sph || 'null'}, CYL: ${leftEye.near.cyl || 'null'}, AXIS: ${leftEye.near.axis || 'null'}\n`;
      }
    }

    return prescriptionText || 'None';
  };

  const generateCompanyOrderMessage = () => {
    if (!orderData) return '';

    const { customer, items, subtotal, shipping, total } = orderData;
    
    let message = `🛒 *NEW ORDER RECEIVED - Mughal Ainak Mahal*\n\n`;
    message += `👤 *Customer Details:*\n`;
    message += `Name: ${customer.firstName} ${customer.lastName}\n`;
    message += `Phone: ${customer.phone}\n`;
    message += `Email/Contact: ${customer.contact || 'N/A'}\n\n`;
    
    message += `📦 *Order Items:*\n`;
    items.forEach((item, index) => {
      message += `${index + 1}. ${item.name}\n`;
      message += `   Quantity: ×${item.quantity}\n`;
      message += `   Price: ${formatPrice(item.price)} each\n`;
      message += `   Subtotal: ${formatPrice(item.price * item.quantity)}\n`;
      
      if (item.prescription) {
        const prescription = formatPrescriptionDetails(item.prescription, item.productType);
        if (prescription !== 'None') {
          message += `   Prescription:\n${prescription}`;
        }
      }
      message += `\n`;
    });

    message += `📊 *Order Summary:*\n`;
    message += `Subtotal: ${formatPrice(subtotal)}\n`;
    message += `Shipping: ${formatPrice(shipping)}\n`;
    message += `*Total: ${formatPrice(total)}*\n\n`;
    
    message += `📍 *Delivery Address:*\n`;
    message += `${customer.address}`;
    if (customer.apartment) message += `, ${customer.apartment}`;
    message += `\n${customer.city}`;
    if (customer.postalCode) message += `, ${customer.postalCode}`;
    message += `\n${customer.country}\n\n`;
    
    message += `💳 *Payment Method:* Cash on Delivery\n\n`;
    message += `Please process this order and contact the customer to confirm delivery.`;

    return encodeURIComponent(message);
  };

  // Format phone numbers for WhatsApp (remove all non-numeric, handle Pakistan numbers)
  const formatPhoneForWhatsApp = (phone) => {
    let cleanPhone = phone.replace(/[^\d+]/g, '');
    // If starts with 0, replace with 92 (Pakistan country code)
    if (cleanPhone.startsWith('0')) {
      cleanPhone = '92' + cleanPhone.substring(1);
    } else if (cleanPhone.startsWith('+92')) {
      cleanPhone = cleanPhone.substring(1); // Remove + sign
    } else if (!cleanPhone.startsWith('92')) {
      cleanPhone = '92' + cleanPhone;
    }
    return cleanPhone;
  };

  useEffect(() => {
    // Only run once on mount and only save if not already saved
    const orderDataFromState = location.state?.orderData;
    if (orderDataFromState && !orderSaved.current) {
      setOrderData(orderDataFromState);
      
      // Track Purchase event for Meta Pixel
      if (window.fbq) {
        const orderValue = orderDataFromState.total || 0;
        const contentIds = orderDataFromState.items.map(item => item.id.toString());
        window.fbq('track', 'Purchase', {
          value: orderValue,
          currency: 'PKR',
          content_ids: contentIds,
          content_type: 'product',
          num_items: orderDataFromState.items.reduce((sum, item) => sum + item.quantity, 0)
        });
      }
      
      orderSaved.current = true;
      
      // Save order to localStorage (persists after refresh)
      try {
        saveOrder(orderDataFromState);
        console.log('Order saved to localStorage');
      } catch (error) {
        console.error('Failed to save order:', error);
      }
      // Clear cart after successful order
      clearCart();
    } else if (!orderDataFromState) {
      // If no order data, redirect to home
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update product quantities in Strapi after order (only once)
  useEffect(() => {
    if (orderData && !stockUpdated.current) {
      stockUpdated.current = true;
      
      // Update stock for each item in the order
      const updateStocks = async () => {
        console.log('Starting stock update for order items:', orderData.items);
        
        const results = await Promise.allSettled(
          orderData.items.map(async (item) => {
            try {
              console.log(`Checking stock for product ${item.id} (${item.name})...`);
              
              // Fetch current product data using getProduct (returns array structure)
              const productData = await getProduct(item.id);
              const productItem = productData.data && productData.data.length > 0 ? productData.data[0] : null;
              
              if (!productItem) {
                console.error(`Product ${item.id} not found`);
                throw new Error(`Product ${item.id} not found`);
              }
              
              const currentQuantity = productItem.quantity !== undefined ? productItem.quantity : 0;
              console.log(`Current quantity for ${item.name}: ${currentQuantity}, ordered: ${item.quantity}`);
              console.log(`Product data:`, productItem);
              
              // Calculate new quantity (decrease by ordered quantity)
              const newQuantity = Math.max(0, currentQuantity - item.quantity);
              console.log(`Updating ${item.name} quantity from ${currentQuantity} to ${newQuantity}...`);
              
              // Use documentId if available (Strapi v4), otherwise use id
              const updateId = productItem.documentId || productItem.id || item.id;
              console.log(`Using ID for update: ${updateId} (documentId: ${productItem.documentId}, id: ${productItem.id})`);
              
              // Update product quantity in Strapi
              const result = await updateProductQuantity(item.id, newQuantity, updateId);
              console.log(`✅ Successfully updated stock for ${item.name}: ${currentQuantity} -> ${newQuantity}`, result);
              return { success: true, item: item.name, oldQty: currentQuantity, newQty: newQuantity };
            } catch (error) {
              console.error(`❌ Failed to update stock for product ${item.id} (${item.name}):`, error);
              return { success: false, item: item.name, error: error.message };
            }
          })
        );
        
        // Log summary
        const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
        const failed = results.length - successful;
        console.log(`Stock update summary: ${successful} successful, ${failed} failed`);
        
        if (failed > 0) {
          console.warn('Some products could not be updated. Please check Strapi API permissions.');
        }
      };
      
      updateStocks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData]);

  // Auto-open WhatsApp and send email notification (only once)
  useEffect(() => {
    if (orderData && !whatsAppOpened.current) {
      whatsAppOpened.current = true;
      
      // Send email notification automatically
      if (!emailSent.current) {
        emailSent.current = true;
        sendOrderNotificationEmail(orderData)
          .then((result) => {
            if (result.success) {
              console.log('Order notification email sent successfully');
            } else {
              console.error('Failed to send email:', result.error);
            }
          })
          .catch((error) => {
            console.error('Error sending email:', error);
          });
      }
      
      // Open WhatsApp after a short delay to ensure page loads
      const companyPhone = formatPhoneForWhatsApp(COMPANY_PHONE);
      const orderMessage = generateCompanyOrderMessage();
      const whatsAppUrl = `https://wa.me/${companyPhone}?text=${orderMessage}`;
      
      const timer = setTimeout(() => {
        window.open(whatsAppUrl, '_blank');
      }, 500);

      // Cleanup timer on unmount
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData]);

  if (!orderData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Order Confirmed - Mughal Ainak Mahal</title>
        <meta name="description" content="Your order has been confirmed" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Success Message */}
        <div className="bg-white rounded-lg shadow-lg p-8 text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-secondary mb-4">Order Confirmed!</h1>
          <p className="text-gray-600 text-lg mb-6">
            Thank you for your order, {orderData.customer.firstName}! Your order has been received.
          </p>
          <p className="text-gray-500">
            Order Total: <span className="font-bold text-primary text-xl">{formatPrice(orderData.total)}</span>
          </p>
        </div>


        {/* Order Summary */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-secondary mb-4">Order Summary</h2>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Customer Name:</span>
              <span className="font-semibold">{orderData.customer.firstName} {orderData.customer.lastName}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Phone:</span>
              <span className="font-semibold">{orderData.customer.phone}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Delivery Address:</span>
              <span className="font-semibold text-right max-w-xs">
                {orderData.customer.address}
                {orderData.customer.apartment && `, ${orderData.customer.apartment}`}
                <br />
                {orderData.customer.city}
                {orderData.customer.postalCode && `, ${orderData.customer.postalCode}`}
              </span>
            </div>
            <div className="flex justify-between text-sm pt-3 border-t border-gray-200">
              <span className="text-gray-600">Total Items:</span>
              <span className="font-semibold">{orderData.items.reduce((sum, item) => sum + item.quantity, 0)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-3 border-t border-gray-200">
              <span>Total Amount:</span>
              <span className="text-primary">{formatPrice(orderData.total)}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate('/')}
            className="bg-secondary text-white py-3 px-8 rounded-lg font-semibold hover:bg-secondary-dark transition-colors duration-200 flex items-center justify-center"
          >
            <Home className="w-5 h-5 mr-2" />
            Back to Home
          </button>
          <button
            onClick={() => navigate('/my-orders')}
            className="bg-primary text-white py-3 px-8 rounded-lg font-semibold hover:bg-primary-dark transition-colors duration-200 flex items-center justify-center"
          >
            <Package className="w-5 h-5 mr-2" />
            My Orders
          </button>
        </div>

      </div>
    </div>
  );
};

export default CheckoutSuccess;

